// istanbul ignore file
import { QuoteStatus } from './quote';

export enum ReportedFindingStatus {
    OPEN = 'OPEN',
    RESOLVED = 'RESOLVED',
    RESOLVED_BY_THIRD_PARTY = 'RESOLVED_BY_THIRD_PARTY',
    ADDITIONAL_INFO_PROVIDED = 'ADDITIONAL_INFO_PROVIDED',
    REVIEW_REQUIRED = 'REVIEW_REQUIRED'
}

export enum ExternalSolutionOffered {
    PROPERTY_BUILT_YEAR = '1',
    INFORM_DI = '2'
}

export enum ReportedFindingsFacets {
    PendingItems = 'PENDING_ITEMS',
    ComplianceIssues = 'COMPLIANCE_ISSUES',
    Compliant = 'COMPLIANT',
    LegacyPendingItems = 'LEGACY_PENDING_ITEMS',
    AwaitingQuotationApproval = 'AWAITING_QUOTATION_APPROVAL',
    AwaitingUrgentQuotationApproval = 'AWAITING_URGENT_QUOTATION_APPROVAL',
    RejectedQuotes = 'QUOTATION_REJECTED'
}

export type ReportedFinding = {
    property: Property;
    reportedFindings: ReportedFindingData[];
    service: Service;
    tags: string[];
};

export type ReportedFindingMachine = ReportedFinding & {
    reportedFindings: ReportedFindingDataMachine[];
};

export type ReportedFindingTransform = ReportedFindingMachine;

type Service = {
    serviceId: number;
    name: string;
    suffix: string;
    tags: string[];
};

type Property = {
    compliancePropertyId: number;
    compliancePropertyManagerId: number;
    addressLine1: string;
    addressLine2: string;
    fullAddress: string;
    propertySkills: string;
    applianceLocation: string;
    applianceName: string;
    propertyBuilt: string | null;
};

export type ReportedFindingData = {
    problemRegister: ProblemRegister;
    servicingCategory: ServicingCategory;
    servicingProblem: ServicingProblem;
    originalProblemItem: OriginalProblemItem;
    servicingCategoryType: ServicingCategoryType;
    quotation: Quotation;
    appliance?: Appliance;
};

export type ProblemRegisterMachine = ProblemRegister & {
    requestSent: boolean;
    canRespond: boolean;
    canRequestQuote: boolean;
    canForwardToLandlord: boolean;
    canInformDI: boolean;
    canEnterBuiltYear: boolean;
    thirdPartyInvalidStatus: boolean;
};

export type ReportedFindingDataMachine = ReportedFindingData & {
    problemRegister: ProblemRegisterMachine;
};

type ProblemRegister = {
    problemRegisterId: number;
    dateResolved: null | string;
    originalBookingDate: string;
    latestServiceDate: string;
    status: string;
    serviceCode: string;
    isDismissed?: boolean;
    dateDismissed?: null | string;
    dismissedBy?: null | string;
    isDismissible?: | boolean;
    quoteRequested?: boolean;
    dateQuoteRequested?: null | string;
    quoteRequestedBy?: null | string;
};

export type Quotation = {
    quotationId: number | null;
    quotationStatusCode: string | QuoteStatus | null;
    url: null | string;
    serviceCode: string | null;
    expiryDate: string | Date | null;
};

export type ServicingCategory = {
    servicingCategoryId: number;
    name: string;
    sortOrder: number;
    description: string;
    color: string;
    active: boolean;
    isAgencyActionHidden: boolean;
};

export type ServicingCategoryType = {
    servicingCategoryTypeId: number;
    name: string;
    color: string;
};

type ServicingProblem = {
    servicingProblemId: number;
    problemTitle: string;
    isQuotable: boolean;
    externalSolutionOffered: ExternalSolutionOffered[];
};

type Appliance = {
    applianceName: string;
    applianceLocation: string;
};

export type ThirdPartyResolutionForm = {
    selectedProblemRegisterIds: number[];
    notes: string;
    documentIds: number[];
};

export type DropdownServicingCategory = {
    id: string;
    name: string;
};

type OriginalProblemItem = {
    originalProblemItemId: number;
    title: string;
};

export enum ReportedFindingServiceTag {
    Overdue = 'OVERDUE',
    ServicePending = 'SERVICE_PENDING',
    BookedIn = 'BOOKED_IN',
    RebookingInProgress = 'REBOOKING_IN_PROGRESS'
}

export enum ReportedFindingViewMode {
    All = 'all', // show all reported findings for single property
    Wizard = 'wizard' // for reported findings wizard modal
}

export type TemporaryDocumentUploadResponse = {
    id: string;
    fileName: string;
    fileMimetype: string;
    fileSizeBytes: number;
};

export { Property as ReportedFindingProperty };
